/* eslint-disable no-undef */
import React, { useState, useEffect, useCallback } from 'react';
import Autocomplete, {
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
} from '@mui/material/Autocomplete';

import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { CircularProgress, TextField } from '@mui/material';

interface AddressAutocompleteProps {
  currentValue?: string;
  // eslint-disable-next-line no-unused-vars
  onAddressChanged: (address: google.maps.places.PlaceResult) => void;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  clearable?: boolean;
  debounceDelay?: number;
}

export type PlacePrediction = google.maps.places.AutocompletePrediction;
export type PlaceResult = google.maps.places.PlaceResult;

const usePlacesService = () => {
  const map = useMap();
  const placesLibrary = useMapsLibrary('places');
  const [placesService, setPlacesService] =
    useState<google.maps.places.AutocompleteService | null>(null);

  useEffect(() => {
    if (!placesLibrary) return;
    setPlacesService(new placesLibrary.AutocompleteService());
  }, [placesLibrary, map]);

  return placesService;
};

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  currentValue,
  placeholder = 'Search places',
  helperText = '',
  error = false,
  onAddressChanged,
  clearable = false,
  debounceDelay = 400,
}) => {
  const placesService = usePlacesService();
  const [value, setValue] = useState(currentValue || '');
  const [baseValue, setBaseValue] = useState(currentValue || '');
  const [placePredictions, setPlacePredictions] = useState<PlacePrediction[]>(
    [],
  );
  const [loading, setLoading] = useState(false);

  const debounce = useCallback(
    // eslint-disable-next-line no-unused-vars
    (func: (...args: string[]) => void, delay: number) => {
      let timeoutId: NodeJS.Timeout;
      return (...args: string[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func(...args), delay);
      };
    },
    [],
  );

  const getPlacePredictionsDebounced = useCallback(
    debounce((input: string) => {
      if (!placesService || !input) return;

      setLoading(true);
      placesService.getPlacePredictions(
        {
          types: ['establishment'],
          input,
        },
        (results, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && results) {
            setPlacePredictions(results);
          } else {
            setPlacePredictions([]);
          }
          setLoading(false);
        },
      );
    }, debounceDelay),
    [placesService, debounce, debounceDelay],
  );

  useEffect(() => {
    if (placesService && value) {
      getPlacePredictionsDebounced(value);
    }
  }, [value, placesService, getPlacePredictionsDebounced]);

  const handleSelect = (prediction: PlacePrediction) => {
    const displayText = `${prediction.structured_formatting.main_text} ${prediction.structured_formatting.secondary_text}`;
    setBaseValue(displayText);
    setValue(displayText);
    onAddressChanged(prediction);

    const detailedPlaceService = new google.maps.places.PlacesService(
      document.createElement('div'),
    );

    detailedPlaceService.getDetails(
      { placeId: prediction.place_id },
      (
        placeResult: google.maps.places.PlaceResult | null,
        status: google.maps.places.PlacesServiceStatus,
      ) => {
        if (
          status === google.maps.places.PlacesServiceStatus.OK &&
          placeResult
        ) {
          onAddressChanged(placeResult);
        } else {
          console.error('Failed to fetch place details:', status);
        }
      },
    );
  };

  return (
    <Autocomplete
      freeSolo
      options={placePredictions}
      inputValue={value}
      getOptionLabel={(option: string | PlacePrediction) => {
        if (typeof option === 'string') {
          return option;
        }
        return `${option.structured_formatting.main_text} ${option.structured_formatting.secondary_text}`;
      }}
      onInputChange={(
        _,
        inputVal: string,
        reason: AutocompleteInputChangeReason,
      ) => {
        if (reason !== 'reset') {
          setValue(inputVal);
          getPlacePredictionsDebounced(inputVal);
        }

        if (clearable && (reason === 'clear' || inputVal === '')) {
          setBaseValue('');
        }
      }}
      onBlur={() => {
        if (baseValue !== value) {
          setValue(baseValue);
        }
      }}
      onFocus={() => {
        if (value) getPlacePredictionsDebounced(value);
      }}
      onChange={(
        _,
        value: string | PlacePrediction | null,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _reason: AutocompleteChangeReason,
      ) => {
        if (value && typeof value !== 'string') {
          handleSelect(value);
        }
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={placeholder}
          variant="outlined"
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={20} sx={{ mr: 2 }} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}></Autocomplete>
  );
};

export { AddressAutocomplete };
